/**
 * @module components/Image
 * @description Lazy-loading image component with viewport detection and Gatsby integration.
 * Uses vsbl library for efficient viewport detection and supports both standard images
 * and Gatsby/Sanity fluid images for responsive layouts.
 */

import React from 'react';
import vsbl from 'vsbl';
import cx from 'classnames';

import Img from 'gatsby-image';
import { getFluidGatsbyImage } from 'gatsby-source-sanity';

const sanityConfig = {
  projectId: process.env.REACT_APP_SANITY_PROJECT_ID,
  dataset: process.env.REACT_APP_SANITY_DATASET,
};

/**
 * @typedef ImageProps
 * @property {string} className - CSS className to apply
 * @property {string} alt - Alt text for accessibility
 * @property {string} source - Image source URL
 * @property {boolean} [defer] - Defer loading until in viewport
 * @property {number} [timeOut=200] - Delay in ms before loading (when not deferred)
 * @property {string} [imageId] - Sanity image ID for fluid images
 */

/**
 * @component Image
 * @type {React.Component<ImageProps>}
 * @summary Lazy-loading image component with viewport detection.
 * Automatically defers image loading until visible in viewport using vsbl library.
 * Supports Gatsby fluid images for Sanity CMS integration.
 * @param {ImageProps} props - Component props
 * @returns {React.ReactElement} Image wrapper with loading states
 */
class Image extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      src: `${
        /insta/.test(this.props.source)
          ? this.props.source
          : `${this.props.source}`
      }`,
      visible: false,
      loaded: false,
      mounted: false,
    };

    this.vs = null;

    this.image = React.createRef();
  }

  componentDidMount() {
    if (!this.props.defer) {
      setTimeout(() => {
        this.setState({
          mounted: true,
        });
      }, this.props.timeOut || 200);
    }

    /**
     * @NOTE -- determines if image is within viewport
     *  - `vsbl` is a lib to detect "in-viewport" without event listeners
     */
    this.vs = vsbl(this.image.current)(() => {
      if (this.state.visible) return;
      if (this.props.defer) {
        this.setState({
          mounted: true,
        });
      }
      setTimeout(() => {
        this.setState({
          visible: true,
        });
      }, 400);
    });

    this.vs.update(); // on page load
  }

  componentWillUnmount() {
    this.vs.destroy();
  }

  render() {
    const { src, visible, loaded, mounted } = this.state;
    const { imageId /* size */ } = this.props;

    let fluidProps;
    if (imageId) {
      fluidProps = getFluidGatsbyImage(
        imageId,
        { maxWidth: 500 },
        sanityConfig,
      );
    }

    return (
      <div
        className={cx(`x y image__block block ${this.props.className}`, {
          background: /jpg/.test(src),
          'is-visible': visible,
        })}
        ref={this.image}
      >
        {fluidProps ? (
          <Img fluid={fluidProps} />
        ) : (
          <img
            alt={this.props.alt}
            src={mounted ? src : null}
            className={cx('x y block', {
              'is-loaded': loaded,
              'is-visible': visible,
            })}
            onLoad={() => {
              this.setState({
                loaded: true,
              });
            }}
          />
        )}
      </div>
    );
  }
}

export default Image;
